import { ChangeDetectionStrategy, Component, effect, input, output, signal, untracked } from "@angular/core";
import { Item } from "../../../pojo/Item";
import { S25qlSearchAdvancedCustomAttributeComponent } from "../../s25-ql/s25ql.search.advanced.custom.attribute.component";
import {
    areSeUserSearchCustomAttributeFiltersEqual,
    collectSeUserSearchCustomAttributeFiltersFromRows,
    emptySeUserSearchCustomAttributeModel,
    normalizeSeUserSearchCustomAttributeModelForEdit,
    resolveSeUserSearchCustomAttributeRowSourceAfterEdit,
    seUserSearchCustomAttributeRowsFromFilters,
    type SeUserSearchCustomAttributeFilter,
    type SeUserSearchCustomAttributeModel,
    type SeUserSearchCustomAttributeRow,
} from "./se.user.search.custom.attribute.util";

/** Shape expected by {@link S25qlSearchAdvancedCustomAttributeComponent#modelValue}. */
type QlSearchCustomAttributeModel = {
    cust_atrb_id: number;
    cust_atrb_name?: string;
    cust_atrb_type: string | number;
    itemId?: string | number;
    itemName?: string;
    relationship_type_id: number;
    relationship_type_name: string;
};

@Component({
    selector: "s25-ng-se-user-search-custom-attribute-filters",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25qlSearchAdvancedCustomAttributeComponent],
    template: `
        <fieldset class="ca-filters">
            <legend class="ca-filters-label">Custom attribute filters</legend>
            @for (row of rows(); track row.rowKey) {
                <div class="ca-filter-row">
                    <div class="ca-filter-editor">
                        <s25-ng-ql-search-advanced-custom-attribute
                            [type]="contactItemType"
                            [modelValue]="modelForRow(row)"
                            (modelValueChange)="onRowModelChange(row.rowKey, $event)"
                        />
                    </div>
                    <button
                        class="ca-filter-remove"
                        type="button"
                        [attr.aria-label]="'Remove custom attribute filter'"
                        (click)="removeRow(row.rowKey)"
                    >
                        Remove
                    </button>
                </div>
            }
            <button
                class="aw-button aw-button--outline aw-button--small ca-filter-add"
                type="button"
                (click)="addRow()"
            >
                Add custom attribute filter
            </button>
        </fieldset>
    `,
    styles: `
        .ca-filters {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            width: 100%;
            border: none;
            padding: 0;
            margin: 0;
            min-width: 0;
        }

        .ca-filters-label {
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--color--text, #333);
            padding: 0;
            margin-bottom: 0.25rem;
            width: 100%;
        }

        .ca-filter-row {
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            flex-wrap: wrap;
            padding: 0.75rem;
            border: 1px solid var(--color--border, #e5e7eb);
            border-radius: 6px;
            background: var(--color--surface-2-bg, #f9fafb);
        }

        .ca-filter-editor {
            flex: 1;
            min-width: 240px;
        }

        .ca-filter-remove {
            background: none;
            border: none;
            color: var(--color--text-muted, #666);
            font-size: 0.875rem;
            cursor: pointer;
            padding: 0.5rem 0.75rem;
            border-radius: 6px;
            white-space: nowrap;
        }

        .ca-filter-remove:hover {
            color: var(--color--text, #333);
            background: var(--color--surface-2-bg, #f0f0f0);
        }

        .ca-filter-add {
            align-self: flex-start;
        }
    `,
})
export class SeUserSearchCustomAttributeFiltersComponent {
    readonly contactItemType = Item.Ids.Contact;

    readonly seedFilters = input<SeUserSearchCustomAttributeFilter[]>([]);
    /** Incremented only on clearFilters to wipe draft rows; pill remove relies on seedFilters sync. */
    readonly resetKey = input(0);

    readonly filtersChange = output<SeUserSearchCustomAttributeFilter[]>();

    readonly rows = signal<SeUserSearchCustomAttributeRow[]>([]);

    private nextRowKey = 0;
    /** Last completed filters pushed to the parent (applied), not draft row edits. */
    private lastEmittedApplied: SeUserSearchCustomAttributeFilter[] | undefined;

    constructor() {
        // Partial sync: pill × and parent seed changes preserve draft rows.
        effect(() => {
            const applied = this.seedFilters();
            untracked(() => this.syncRowsFromApplied(applied));
        });

        // Full reset: clearFilters only (registered second so it wins on the same tick).
        effect(() => {
            const resetKey = this.resetKey();
            untracked(() => {
                if (resetKey === 0) {
                    return;
                }
                this.rows.set([]);
                this.nextRowKey = 0;
                this.lastEmittedApplied = undefined;
            });
        });
    }

    modelForRow(row: SeUserSearchCustomAttributeRow): QlSearchCustomAttributeModel {
        return row.model as QlSearchCustomAttributeModel;
    }

    addRow(): void {
        this.nextRowKey += 1;
        this.rows.update((current) => [
            ...current,
            {
                rowKey: this.nextRowKey,
                model: emptySeUserSearchCustomAttributeModel(),
                source: "draft",
            },
        ]);
    }

    removeRow(rowKey: number): void {
        this.rows.update((current) => current.filter((row) => row.rowKey !== rowKey));
        this.emitAppliedFiltersIfChanged();
    }

    onRowModelChange(rowKey: number, model: SeUserSearchCustomAttributeModel): void {
        this.rows.update((current) =>
            current.map((row) => {
                if (row.rowKey !== rowKey) {
                    return row;
                }
                const normalizedModel = normalizeSeUserSearchCustomAttributeModelForEdit(model);
                return {
                    ...row,
                    model: normalizedModel,
                    source: resolveSeUserSearchCustomAttributeRowSourceAfterEdit(normalizedModel, row.source),
                };
            }),
        );
        this.emitAppliedFiltersIfChanged();
    }

    private syncRowsFromApplied(applied: SeUserSearchCustomAttributeFilter[]): void {
        const appliedKeys = new Set(applied.map((filter) => filter.filterKey));
        const appliedRows = seUserSearchCustomAttributeRowsFromFilters(applied);
        const draftRows = this.rows().filter((row) => row.source === "draft" && !appliedKeys.has(String(row.rowKey)));
        this.rows.set([...appliedRows, ...draftRows]);
        this.nextRowKey = Math.max(0, ...this.rows().map((row) => row.rowKey));
        this.lastEmittedApplied = applied.length ? [...applied] : undefined;
    }

    private emitAppliedFiltersIfChanged(): void {
        const completed = collectSeUserSearchCustomAttributeFiltersFromRows(this.rows());
        const nextApplied = completed.length ? completed : undefined;
        if (areSeUserSearchCustomAttributeFiltersEqual(this.lastEmittedApplied, nextApplied)) {
            return;
        }
        this.lastEmittedApplied = nextApplied ? [...nextApplied] : undefined;
        this.filtersChange.emit(completed);
    }
}
